import { getTranslationKeyFunction } from '~/utils/commonUtils.ts';
import { PasskeyCredential } from '~/api/types/passkeysData.ts';
import { getLocalizedString } from '~/i18n/utils.ts';
import { ConfirmModalValues } from '~/components/ui/Modal/ConfirmModalContext.tsx';
import { ToastContextValue } from '~/components/ui/Toast';
import { QueryClient } from '@tanstack/solid-query';
import { getPasskeyCredentialsListQueryKey } from '~/api';
import {
  deletePasskeyCredential,
  DeletePasskeyCredentialResponse
} from '~/api/resources/passkeys.ts';
import { UserData } from '~/api/types/userData.ts';

const TRANSLATION_BASE_PATH = 'pages.external.passkeys';

const getTranslationKey = getTranslationKeyFunction(TRANSLATION_BASE_PATH);

const MAX_PASSKEY_CREDENTIALS = 5;

const getPasskeyFeatureConfig = (intl: any) => [
  {
    header: getLocalizedString(getTranslationKey('passkey_features.header_1'), {
      intl
    }),
    subheader: getLocalizedString(
      getTranslationKey('passkey_features.subheader_1'),
      { intl }
    ),
    id: `security_and_ease`
  },
  {
    header: getLocalizedString(getTranslationKey('passkey_features.header_2'), {
      intl
    }),
    subheader: getLocalizedString(
      getTranslationKey('passkey_features.subheader_2'),
      { intl }
    ),
    id: `security`
  }
];

const getCreatedAtContext = (credential: PasskeyCredential, intl: any) => {
  const { browser, os } = credential.userAgent ?? {};
  const { city, region } = credential.clientInfo ?? {};

  const onText = getLocalizedString(getTranslationKey('passkey_on'), { intl });
  const inText = getLocalizedString(getTranslationKey('passkey_in'), { intl });

  const parts: string[] = [];

  if (browser && os) parts.push(`${browser} ${onText} ${os}`);
  else if (browser) parts.push(browser);
  else if (os) parts.push(`${onText} ${os}`);

  if (city && region) parts.push(`${inText} ${city}, ${region}`);
  else if (city) parts.push(`${inText} ${city}`);
  else if (region) parts.push(`${inText} ${region}`);

  return parts.length ? `, ${parts.join(' ')}` : '';
};

const deletePasskeyCredentialWithConfirmation = ({
  credential,
  user,
  intl,
  confirm,
  toaster,
  queryClient
}: {
  credential: PasskeyCredential;
  user?: UserData;
  intl: any;
  confirm: ConfirmModalValues;
  toaster: ToastContextValue;
  queryClient: QueryClient;
}): Promise<DeletePasskeyCredentialResponse | null> => {
  return new Promise((resolve, reject) => {
    confirm.show({
      title: getLocalizedString(
        getTranslationKey('delete_passkey_confirm_title'),
        {
          intl
        }
      ),
      description: getLocalizedString(
        getTranslationKey('delete_passkey_confirm_description'),
        {
          intl,
          name: credential.displayName ?? credential.authenticator.name
        }
      ),
      primaryActionDataTestId: 'passkey-delete',
      primaryActionLabel: getLocalizedString('cta_labels.delete', { intl }),
      onConfirm: async () => {
        try {
          const result = await deletePasskeyCredential({ id: credential.id });
          confirm.setIsLoading(false);
          confirm.close();
          toaster.toast().success({
            description: getLocalizedString(
              getTranslationKey('passkey_delete_success_toast'),
              {
                intl,
                name: credential.displayName ?? credential.authenticator.name
              }
            )
          });
          await queryClient.refetchQueries({
            queryKey: getPasskeyCredentialsListQueryKey(user?.id),
            type: 'all',
            exact: true
          });
          resolve(result);
        } catch (e) {
          confirm.setIsLoading(false);
          reject(e);
        }
      },
      onCancel: () => {
        confirm.setIsLoading(false);
        resolve(null);
      }
    });
  });
};

export {
  MAX_PASSKEY_CREDENTIALS,
  getTranslationKey,
  getCreatedAtContext,
  getPasskeyFeatureConfig,
  deletePasskeyCredentialWithConfirmation
};
